/**
 * Configuration for the fake-streaming message reveal effect.
 * All timing values in one place for easy tuning.
 */

import type { ChatMessageType } from '@/lib/markdown';

export const MESSAGE_REVEAL_CONFIG = {
  /** Characters revealed per animation frame tick */
  charsPerTick: 1,
  /** Milliseconds between ticks — 1 char / 30ms ≈ ~33 chars/sec ≈ ~40 WPM */
  tickIntervalMs: 30,
  /** Pause between finishing one bubble and starting the next */
  delayBetweenMessagesMs: 0,
  /** Hard cap on any single message's reveal animation */
  maxAnimationMs: 4500,
  /** Minimum animation time even for very short messages */
  minAnimationMs: 800,
};

/**
 * Game-session reveal profile, much faster than the lobby's humanlike
 * 40 WPM. Game bubbles are narration/dialogue the player wants to read
 * promptly; the typewriter exists to pace bubbles one-by-one (so a
 * round_result doesn't dump the whole round at once), not to imitate a
 * human typing. 4 chars / 24ms ≈ 167 chars/sec, capped at 4.5s per
 * bubble for long narration.
 */
export const GAME_REVEAL_CONFIG = {
  charsPerTick: 4,
  tickIntervalMs: 24,
  delayBetweenMessagesMs: 0,
  maxAnimationMs: 4500,
  minAnimationMs: 0,
};

export function revealConfigFor(container: 'lobby' | 'game') {
  return container === 'game' ? GAME_REVEAL_CONFIG : MESSAGE_REVEAL_CONFIG;
}

/**
 * Lobby chat surfaces a small set of NPC personalities (Kira, Aldric, Grolt,
 * generic `npc`) who pretend to be fellow human players. Only those authors,
 * and only in lobby context, get the typewriter — Tavian (staff), system
 * notices, and user messages render instantly.
 */
const LOBBY_NPC_TYPEWRITER_TYPES = new Set<ChatMessageType>(['aldric', 'kira', 'grolt', 'npc']);

/**
 * Game-session queued reveals (round_result bubbles): DM narration,
 * character/NPC/monster actions, and whispers all typewriter at the fast
 * GAME_REVEAL_CONFIG pace so the round unfolds bubble-by-bubble. The live
 * `dm-narrative-stream-*` bubble bypasses this (StreamingMarkdown, real
 * server pacing); system/error bubbles render instantly.
 */
const GAME_TYPEWRITER_TYPES = new Set<ChatMessageType>(['dm', 'user', 'whisper']);

export function shouldTypewriter(
  container: 'lobby' | 'game',
  type: ChatMessageType | undefined
): boolean {
  if (!type) return false;
  if (container === 'game') return GAME_TYPEWRITER_TYPES.has(type);
  return LOBBY_NPC_TYPEWRITER_TYPES.has(type);
}
